import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';

import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildScatterBubbleOption } from './unified-scatter-bubble-option.builder';
import type { ScatterBubbleCapabilities, ScatterBubbleWidgetData } from './unified-scatter-bubble-chart.types';

/**
 * Unified Scatter & Bubble chart — ONE ECharts component for the whole family
 * (Scatter / Multi Scatter / Bubble / Quadrant, + advanced variants declared in
 * config). All variation lives in `data.capabilities` (ScatterBubbleCapabilities);
 * the PURE `buildScatterBubbleOption` turns capabilities + flat rows into the
 * option and this component is just the lifecycle shell (init / dispose / resize /
 * theme), mirroring the unified Bar component so the format popup, cross-filter
 * registry and published renderer reuse identically.
 */
@Component({
  selector: 'app-unified-scatter-bubble-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './unified-scatter-bubble-chart.component.html',
  styleUrl: './unified-scatter-bubble-chart.component.scss',
})
export class UnifiedScatterBubbleChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: ScatterBubbleWidgetData | Record<string, unknown>;

  /** Emitted on point click when `capabilities.emitSelection` is on. */
  @Output() selection = new EventEmitter<{ category: string; series?: string; value: number }>();

  /** Emitted on point click when `capabilities.crossFilter` is on — the column +
   *  the FULL current selection (multi-select). Plugs into the DVM cross-filter.
   *  The published column value is the clicked point's color-group / series name. */
  @Output() crossFilterChange = new EventEmitter<{ column: string; value: string | null; values: string[] }>();

  protected myChart!: echarts.ECharts;
  private crossFilterValues = new Set<string>();

  @ViewChild('sbHost', { static: false }) private hostRef?: ElementRef<HTMLDivElement>;

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private lastRows: Array<Record<string, unknown>> = [];
  /** Color-by values seen so far (first-seen order, for `seenColorField`) — a
   *  filtered re-render keeps each category's palette slot instead of
   *  re-indexing the survivors (Short Term stays green when Long Term is
   *  filtered out). */
  private seenColorOrder: string[] = [];
  private seenColorField = '';

  private get cfg(): ScatterBubbleWidgetData {
    return (this.data || {}) as ScatterBubbleWidgetData;
  }

  /** CSS height for the host div (coerce raw numbers to px like the Bar shell). */
  get hostHeight(): string {
    const h = this.cfg.height;
    if (h === undefined || h === null || h === '') return 'var(--dvm-chart-height, 300px)';
    if (typeof h === 'number') return `${h}px`;
    return /^\d+$/.test(String(h)) ? `${h}px` : String(h);
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => [],
    });

    const staticRows = this.resolveStaticRows();
    if (staticRows) {
      setTimeout(() => this.renderRows(staticRows), 0);
    } else {
      setTimeout(() => this.loadAndRenderChart(initialFilters ?? undefined), 0);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    const host = this.resolveHost();
    if (host && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
      this.resizeObserver.observe(host);
    }

    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.myChart || !this.lastRows.length) return;
      const chartDom = this.resolveHost();
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderRows(this.lastRows);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || changes['data'].isFirstChange()) return;
    if (!this.myChart) return;
    const staticRows = this.resolveStaticRows();
    this.renderRows(staticRows ?? this.lastRows);
  }

  private resolveHost(): HTMLElement | null {
    return this.hostRef?.nativeElement || document.getElementById(this.cfg.widgetId || '');
  }

  private resolveStaticRows(): Array<Record<string, unknown>> | null {
    const candidate = this.cfg.staticData ?? this.cfg.rows ?? this.cfg.data;
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    const nested = (candidate as { data?: unknown })?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : null;
  }

  protected loadAndRenderChart(filters?: unknown): void {
    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    const staticRows = this.resolveStaticRows();
    if (staticRows) { this.renderRows(staticRows); return; }
    if (!this.cfg.apiUrl) { this.renderRows([]); return; }

    this.myChart.showLoading();
    this.configService.getConfig(this.cfg.apiUrl, this.data, filters)?.subscribe({
      next: (res: unknown) => {
        const raw = (res as { data?: unknown })?.data ?? res;
        this.renderRows(Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : []);
        this.myChart.hideLoading();
      },
      error: () => this.myChart.hideLoading(),
    });
  }

  private renderRows(rows: Array<Record<string, unknown>>): void {
    const capabilities = this.cfg.capabilities;
    if (!capabilities) return;
    this.lastRows = rows;

    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    // The live canvas size lets the builder pad the axes so whole bubbles stay
    // inside the plot (read at ECharts layout time, so it tracks resizes).
    const option = buildScatterBubbleOption(rows, this.withStableColorOrder(capabilities, rows), {
      getPlotSize: () => (this.myChart ? { width: this.myChart.getWidth(), height: this.myChart.getHeight() } : null),
    }) as Record<string, unknown>;
    applyRoleVisibilityToOption(option, this.data);
    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.myChart.resize();
    setTimeout(() => this.myChart?.resize(), 60);
    this.addResizeListener();
    this.wrapper.afterRender();
    this.myChart.off('legendselectchanged');
    this.setupInteractions();
  }

  /** Capabilities with `categoryOrder` extended by every Color-by value this
   *  instance has rendered: the baked (build-time) order leads, then values in
   *  the order they were first seen. Reset when the Color column changes. */
  private withStableColorOrder(capabilities: ScatterBubbleCapabilities, rows: Array<Record<string, unknown>>): ScatterBubbleCapabilities {
    const colorField = capabilities.colorField || '';
    if (!colorField) return capabilities;
    if (colorField !== this.seenColorField) {
      this.seenColorField = colorField;
      this.seenColorOrder = [];
    }
    const order = [...(capabilities.categoryOrder || []).map(String)];
    const known = new Set(order);
    const add = (value: string) => { if (!known.has(value)) { known.add(value); order.push(value); } };
    this.seenColorOrder.forEach(add);
    rows.forEach((row) => add(String(row[colorField] ?? '')));
    this.seenColorOrder = order;
    return { ...capabilities, categoryOrder: order };
  }

  /** Wire the Interaction capabilities that need a live click handler. No-op when
   *  none are on. The point series is `scatter`. */
  private setupInteractions(): void {
    const cap = this.cfg.capabilities;
    if (!cap || !this.myChart) return;
    const anyClick = cap.highlightOnClick || cap.crossFilter || cap.emitSelection;
    if (!anyClick) return;
    this.myChart.off('click');

    this.myChart.on('click', (params: { seriesType?: string; seriesName?: string; name?: string; value?: unknown }) => {
      if (params.seriesType !== 'scatter') return;
      // For scatter, the "category" published on click is the point's color-group /
      // series name (Multi Scatter) — the only categorical dimension a point has.
      const category = String(params.name || params.seriesName || '');
      const arr = Array.isArray(params.value) ? (params.value as number[]) : [];
      const value = Number(arr[1] ?? 0);

      if (cap.emitSelection) {
        this.selection.emit({ category, series: cap.series ? params.seriesName : undefined, value });
      }
      if (cap.crossFilter) {
        const column = cap.crossFilterKey || cap.colorField || cap.series || '';
        if (!column || !category) return;
        if (this.crossFilterValues.has(category)) this.crossFilterValues.delete(category);
        else this.crossFilterValues.add(category);
        const values = Array.from(this.crossFilterValues);
        this.crossFilterChange.emit({ column, value: category, values });
      }
    });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
